import {
  IntegrityError,
  acceptSenderHandshake,
  computeManifestCommitment,
  createDiscoveryCommitment,
  createKeyConfirmation,
  derivePhysicalFrameAuthKey,
  hashHex,
  openPhysicalFrameEnvelope,
  openSealedChunk,
  openSealedObject,
  parseCommittedManifest,
  sealPhysicalFrameEnvelope,
  verifyKeyConfirmation,
  type SealedObject,
  type SplitPeerSession,
} from '@spectrabus/crypto';
import { GenerationDecoder } from '@spectrabus/fountain';
import { encodeOpticalPayload, type OpticalFrame } from '@spectrabus/optical-encoder';
import {
  ProtocolError,
  SequenceReplayError,
  assertPhaseTransition,
  decodeTransportPacket,
  equalBytes,
  hexToBytes,
  type ObjectManifest,
  type PhysicalChannel,
  type TransferPhase,
} from '@spectrabus/protocol';
import {
  classifyControlPayload,
  decodeManifestSegment,
  encodeRankFeedback,
  ManifestAssembler,
  peekPhysicalFrameHeader,
  payloadMagic,
  type RankFeedback,
} from './control-frames';
import {
  ACOUSTIC_FORWARD_PROFILE_ID,
  ACOUSTIC_RETURN_PROFILE_ID,
  OPTICAL_PROFILE_ID,
  ReplayWindows,
  SequenceAllocator,
  feedbackContextId,
  liveEvent,
  liveFountainSeed,
  manifestContextId,
  type LiveEvent,
} from './shared';

const SBHS_MAGIC = 0x53424853;
const FORWARD_SILENCE_TIMEOUT_MS = 120_000;
const AUTHORISATION_STALL_TIMEOUT_MS = 300_000;

interface ReceiverGeneration {
  readonly decoder: GenerationDecoder;
  opticalContributions: number;
  acousticContributions: number;
  reconstructed: Uint8Array | null;
  verified: boolean;
}

export interface ChannelTally {
  accepted: number;
  rejected: number;
  duplicate: number;
  independent: number;
}

export interface ReceiverLedger {
  forwardPayloadsAccepted: number;
  forwardPayloadsRejected: number;
  duplicates: number;
  admittedEquations: number;
  independentEquations: number;
  manifestSegmentsSeen: number;
  returnEmissions: number;
  readonly channels: Record<PhysicalChannel, ChannelTally>;
}

export interface RecoveredObject {
  readonly bytes: Uint8Array;
  readonly name: string;
  readonly mimeType: string;
  readonly objectHash: string;
}

/**
 * Accepts a scanned discovery offer and drives real admission: every payload
 * captured from the forward channels flows through authentication, transport
 * decoding, fountain admission and sealed-chunk verification, while the
 * return band carries the key response, confirmations and rank feedback.
 */
export class ReceiverLiveSession {
  #phase: TransferPhase = 'DISCOVER';
  #phaseDetail = 'Offer accepted; emitting the key response on the return band.';
  #failure: string | null = null;
  #session: SplitPeerSession;
  #offerBytes: Uint8Array;
  #responseBytes: Uint8Array;
  #discoveryCommitment: string;
  #frameAuthKey: Uint8Array | null = null;
  #manifest: ObjectManifest | null = null;
  #sealedStandIn: SealedObject | null = null;
  #assembler = new ManifestAssembler();
  #generations = new Map<number, ReceiverGeneration>();
  #seed: string | null = null;
  #sequences = new SequenceAllocator();
  #forwardReplayWindows = new ReplayWindows();
  #confirmationReplayWindows = new ReplayWindows();
  #verifiedForwardChannels = new Set<PhysicalChannel>();
  #returnCycle = 0;
  #result: RecoveredObject | null = null;
  #lastOpticalFrame: OpticalFrame | null = null;
  #lastForwardPayloadAt: number | null = null;
  #forwardAudioDisabled = false;
  #integrityViolations = 0;
  #createdAt: number;
  #phaseEnteredAt: number;
  public readonly ledger: ReceiverLedger = {
    forwardPayloadsAccepted: 0,
    forwardPayloadsRejected: 0,
    duplicates: 0,
    admittedEquations: 0,
    independentEquations: 0,
    manifestSegmentsSeen: 0,
    returnEmissions: 0,
    channels: {
      optical: { accepted: 0, rejected: 0, duplicate: 0, independent: 0 },
      acoustic: { accepted: 0, rejected: 0, duplicate: 0, independent: 0 },
    },
  };

  private constructor(
    session: SplitPeerSession,
    offerBytes: Uint8Array,
    responseBytes: Uint8Array,
    discoveryCommitment: string,
    nowMs: number,
  ) {
    this.#session = session;
    this.#offerBytes = offerBytes.slice();
    this.#responseBytes = responseBytes.slice();
    this.#discoveryCommitment = discoveryCommitment;
    this.#createdAt = nowMs;
    this.#phaseEnteredAt = nowMs;
  }

  public static async accept(
    offerBytes: Uint8Array,
    nowMs = Date.now(),
    options: { readonly forwardAudioDisabled?: boolean } = {},
  ): Promise<ReceiverLiveSession> {
    const handshake = await acceptSenderHandshake(offerBytes, { responseSequence: 0 });
    const receiver = new ReceiverLiveSession(
      handshake.session,
      offerBytes,
      handshake.responseBytes,
      handshake.offer.discoveryCommitment,
      nowMs,
    );
    receiver.#forwardAudioDisabled = options.forwardAudioDisabled === true;
    receiver.#frameAuthKey = await derivePhysicalFrameAuthKey(
      handshake.session.objectRootKey,
      handshake.session.sessionId,
    );
    return receiver;
  }

  public get phase(): TransferPhase {
    return this.#phase;
  }

  public get phaseDetail(): string {
    return this.#phaseDetail;
  }

  public get failure(): string | null {
    return this.#failure;
  }

  public get session(): SplitPeerSession {
    return this.#session;
  }

  public get responseBytes(): Uint8Array {
    return this.#responseBytes.slice();
  }

  public get discoveryCommitment(): string {
    return this.#discoveryCommitment;
  }

  public get manifest(): ObjectManifest | null {
    return this.#manifest;
  }

  public get manifestCommitment(): string | null {
    return this.#sealedStandIn?.manifestCommitment ?? null;
  }

  public get result(): RecoveredObject | null {
    return this.#result;
  }

  public get integrityViolations(): number {
    return this.#integrityViolations;
  }

  /**
   * The last CRC-accepted optical payload re-encoded to its exact on-air
   * cell field. Derived, not sampled: the encoder is deterministic, so this
   * reproduces the raster the sender presented for that payload.
   */
  public get lastOpticalFrame(): OpticalFrame | null {
    return this.#lastOpticalFrame;
  }

  public get verifiedForwardChannels(): readonly PhysicalChannel[] {
    return [...this.#verifiedForwardChannels];
  }

  public get transferAuthorised(): boolean {
    return this.#verifiedForwardChannels.size === this.#session.channels.length;
  }

  public get elapsedMs(): number {
    return Date.now() - this.#createdAt;
  }

  public get generationSummaries(): readonly {
    generationId: number;
    rank: number;
    requiredRank: number;
    opticalContributions: number;
    acousticContributions: number;
    verified: boolean;
  }[] {
    return [...this.#generations.entries()]
      .sort(([left], [right]) => left - right)
      .map(([generationId, generation]) => ({
        generationId,
        rank: generation.decoder.rank,
        requiredRank: generation.decoder.sourceCount,
        opticalContributions: generation.opticalContributions,
        acousticContributions: generation.acousticContributions,
        verified: generation.verified,
      }));
  }

  #transition(next: TransferPhase, detail: string, nowMs: number): void {
    assertPhaseTransition(this.#phase, next);
    this.#phase = next;
    this.#phaseDetail = detail;
    this.#phaseEnteredAt = nowMs;
  }

  #fail(message: string, nowMs: number): void {
    if (this.#phase !== 'FAILED') {
      this.#phase = 'FAILED';
      this.#phaseEnteredAt = nowMs;
    }
    this.#failure = message;
    this.#phaseDetail = message;
  }

  public tick(nowMs = Date.now()): void {
    const preTransfer =
      this.#phase === 'DISCOVER' || this.#phase === 'CALIBRATE' || this.#phase === 'KEY_CONFIRM';
    if (!preTransfer) return;
    // A live pair takes real-world time to aim and adjust volume, so the
    // watchdog distinguishes a dead link from a partially working one and
    // keeps the operator informed instead of failing at the first pause.
    const lastSeen = this.#lastForwardPayloadAt ?? this.#phaseEnteredAt;
    if (nowMs - lastSeen > FORWARD_SILENCE_TIMEOUT_MS) {
      this.#fail(
        'No forward traffic for two minutes. Check that this camera can see the sender raster and re-aim inside the alignment gate.',
        nowMs,
      );
      return;
    }
    if (nowMs - this.#phaseEnteredAt > AUTHORISATION_STALL_TIMEOUT_MS) {
      this.#fail(
        'Forward traffic is visible but mutual authorisation never completed. The sender is probably not hearing this device: raise this speaker volume, move the devices closer and keep the sender microphone uncovered.',
        nowMs,
      );
      return;
    }
    if (this.#lastForwardPayloadAt !== null && this.#phase === 'DISCOVER') {
      this.#phaseDetail =
        'Offer traffic visible; response emitted on the return band — awaiting sender confirmations. If this persists, the sender cannot hear this device: raise volume and move closer.';
    }
  }

  async #handleForwardConfirmation(bytes: Uint8Array, nowMs: number): Promise<LiveEvent[]> {
    const channel: PhysicalChannel = bytes[8] === 2 ? 'acoustic' : 'optical';
    let message;
    try {
      message = await verifyKeyConfirmation(this.#session, bytes, {
        direction: 'sender-to-receiver',
        channel,
        replayWindow: this.#confirmationReplayWindows.for('sender-to-receiver', channel),
      });
    } catch (error) {
      if (error instanceof SequenceReplayError) {
        return [liveEvent('confirmation-duplicate', channel, 'Repeated confirmation burst ignored.')];
      }
      this.ledger.forwardPayloadsRejected += 1;
      this.#integrityViolations += 1;
      return [
        liveEvent('rejected', channel, error instanceof Error ? error.message : 'Confirmation rejected.'),
      ];
    }
    const alreadyVerified = this.#verifiedForwardChannels.has(message.channel);
    this.#verifiedForwardChannels.add(message.channel);
    const events: LiveEvent[] = alreadyVerified
      ? [liveEvent('confirmation-duplicate', message.channel, 'Channel already confirmed.')]
      : [
          liveEvent(
            'confirmation-verified',
            message.channel,
            `Forward direction confirmed on ${message.channel}.`,
          ),
        ];
    if (this.#phase === 'DISCOVER' && !alreadyVerified) {
      this.#transition('CALIBRATE', 'First forward confirmation verified.', nowMs);
      this.#transition('KEY_CONFIRM', 'Verifying per-channel key confirmations.', nowMs);
      events.push(liveEvent('phase-changed', null, 'KEY_CONFIRM'));
    }
    if (this.#phase === 'KEY_CONFIRM' && this.transferAuthorised) {
      this.#transition('TRANSFER', 'Both forward confirmations verified; admitting sealed frames.', nowMs);
      events.push(liveEvent('phase-changed', null, 'TRANSFER'));
    }
    return events;
  }

  async #acceptManifest(manifestBytes: Uint8Array): Promise<LiveEvent[]> {
    const manifest = parseCommittedManifest(manifestBytes);
    const commitment = await createDiscoveryCommitment({
      name: manifest.name,
      mimeType: manifest.mimeType,
      byteLength: manifest.byteLength,
      objectHash: manifest.objectHash,
      chunkSize: manifest.chunkSize,
      compression: manifest.compression,
    });
    if (commitment !== this.#discoveryCommitment) {
      this.#assembler.reset();
      this.#integrityViolations += 1;
      throw new ProtocolError(
        'LIVE_MANIFEST_BINDING',
        'Reassembled manifest does not match the session discovery commitment.',
      );
    }
    this.#manifest = manifest;
    this.#seed = liveFountainSeed(this.#session.sessionId, manifest.objectHash);
    this.#sealedStandIn = {
      descriptor: {
        name: manifest.name,
        mimeType: manifest.mimeType,
        byteLength: manifest.byteLength,
      },
      objectId: manifest.objectId,
      rootKey: this.#session.objectRootKey,
      chunks: [],
      manifest,
      manifestBytes,
      manifestCommitment: await computeManifestCommitment(manifest.objectId, manifestBytes),
    };
    return [
      liveEvent(
        'manifest-verified',
        null,
        `Manifest bound to the discovery commitment across ${manifest.chunkCount} generations.`,
      ),
    ];
  }

  #generationFor(packetGenerationId: number, sourceCount: number, symbolSize: number, validBytes: number): ReceiverGeneration {
    const manifest = this.#manifest;
    const seed = this.#seed;
    if (manifest === null || seed === null) {
      throw new ProtocolError('LIVE_ADMISSION_ORDER', 'Transport frame admitted before the manifest.');
    }
    const entry = manifest.chunks[packetGenerationId];
    if (entry === undefined) {
      throw new ProtocolError('LIVE_SESSION_BINDING', 'Transport frame names a generation outside the manifest.');
    }
    // The packet's own metadata must reproduce the manifest-derived shape;
    // otherwise an attacker who owns the header could desynchronise the
    // decoder against the verified manifest.
    const expectedValidBytes = entry.ciphertextLength;
    const expectedSourceCount = Math.ceil(expectedValidBytes / symbolSize);
    if (validBytes !== expectedValidBytes || sourceCount !== expectedSourceCount) {
      throw new ProtocolError('LIVE_SESSION_BINDING', 'Transport frame shape contradicts the manifest.');
    }
    let generation = this.#generations.get(packetGenerationId);
    if (generation === undefined) {
      generation = {
        decoder: new GenerationDecoder(seed, packetGenerationId, sourceCount, symbolSize, validBytes),
        opticalContributions: 0,
        acousticContributions: 0,
        reconstructed: null,
        verified: false,
      };
      this.#generations.set(packetGenerationId, generation);
    }
    return generation;
  }

  async #verifyGeneration(generationId: number, generation: ReceiverGeneration, nowMs: number): Promise<LiveEvent[]> {
    const sealed = this.#sealedStandIn;
    if (sealed === null || generation.reconstructed === null) return [];
    await openSealedChunk(sealed, generationId, generation.reconstructed, {
      discoveryCommitment: this.#discoveryCommitment,
    });
    generation.verified = true;
    const events = [
      liveEvent('generation-verified', null, `Generation ${generationId} decrypted and hash-verified.`),
    ];
    const manifest = this.#manifest;
    if (
      manifest !== null &&
      this.#generations.size === manifest.chunkCount &&
      [...this.#generations.values()].every((entry) => entry.verified)
    ) {
      events.push(...(await this.#verifyObject(nowMs)));
    }
    return events;
  }

  async #verifyObject(nowMs: number): Promise<LiveEvent[]> {
    const sealed = this.#sealedStandIn;
    const manifest = this.#manifest;
    if (sealed === null || manifest === null) return [];
    const ciphertexts: Uint8Array[] = [];
    for (let index = 0; index < manifest.chunkCount; index += 1) {
      const generation = this.#generations.get(index);
      if (generation?.reconstructed == null) return [];
      ciphertexts.push(generation.reconstructed);
    }
    this.#transition('VERIFY', 'All generations reconstructed; opening the sealed object.', nowMs);
    const plaintext = await openSealedObject(sealed, ciphertexts, {
      discoveryCommitment: this.#discoveryCommitment,
    });
    const objectHash = await hashHex(plaintext);
    this.#result = {
      bytes: plaintext,
      name: manifest.name,
      mimeType: manifest.mimeType,
      objectHash,
    };
    this.#transition('COMPLETE', 'Object opened and verified end to end.', nowMs);
    return [
      liveEvent('object-verified', null, 'Recovered object matches every integrity commitment.'),
      liveEvent('phase-changed', null, 'COMPLETE'),
    ];
  }

  async #handleEnvelope(bytes: Uint8Array, channel: PhysicalChannel, nowMs: number): Promise<LiveEvent[]> {
    const key = this.#frameAuthKey;
    if (key === null) {
      throw new ProtocolError('LIVE_ADMISSION_ORDER', 'Envelope before frame authentication keys.');
    }
    const peeked = peekPhysicalFrameHeader(bytes);
    if (peeked === null || peeked.direction !== 'sender-to-receiver' || peeked.channel !== channel) {
      this.ledger.forwardPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Envelope header is malformed or misdirected.')];
    }
    const profileId = channel === 'optical' ? OPTICAL_PROFILE_ID : ACOUSTIC_FORWARD_PROFILE_ID;
    const manifestContext = manifestContextId(this.#session.transferId);
    const dataContext = this.#manifest?.objectId ?? null;
    const expectationContext =
      peeked.contextId === manifestContext ? manifestContext : dataContext;
    if (expectationContext === null) {
      // Data frames before the manifest cannot be bound yet; the fountain
      // stream is redundant by construction so dropping is safe and honest.
      this.ledger.forwardPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Data frame before the manifest; awaiting manifest segments.')];
    }
    const opened = await openPhysicalFrameEnvelope(bytes, key, {
      sessionId: this.#session.sessionId,
      direction: 'sender-to-receiver',
      channel,
      profileId,
      contextId: expectationContext,
      replayWindow: this.#forwardReplayWindows.for('sender-to-receiver', channel),
    });

    if (!this.transferAuthorised) {
      // Mirrors the reference gate: sealed payload is not admitted before
      // mutual confirmation even though it authenticated correctly.
      this.ledger.forwardPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Sealed frame before mutual key confirmation.')];
    }

    if (channel === 'optical') {
      this.#lastOpticalFrame = encodeOpticalPayload(bytes, opened.binding.sequence);
    }

    const kind = classifyControlPayload(opened.payload);
    if (kind === 'manifest-segment') {
      const segment = decodeManifestSegment(opened.payload);
      this.ledger.manifestSegmentsSeen += 1;
      const added = this.#manifest === null && this.#assembler.add(segment);
      const events: LiveEvent[] = [];
      if (added) {
        events.push(
          liveEvent('manifest-progress', channel, `Manifest segment ${segment.segmentIndex + 1}/${segment.segmentCount}.`),
        );
        const assembled = this.#assembler.takeManifestBytes();
        if (assembled !== null) {
          events.push(...(await this.#acceptManifest(assembled)));
        }
      } else {
        this.ledger.duplicates += 1;
        events.push(liveEvent('equation-duplicate', channel, 'Repeated manifest segment.'));
      }
      this.ledger.forwardPayloadsAccepted += 1;
      return events;
    }
    if (kind !== 'transport') {
      this.ledger.forwardPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Authenticated payload has an unknown control magic.')];
    }

    const packet = decodeTransportPacket(opened.payload);
    const manifest = this.#manifest;
    if (manifest === null) {
      this.ledger.forwardPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Transport frame before the manifest.')];
    }
    if (
      !equalBytes(packet.sessionTag, hexToBytes(this.#session.sessionId).slice(0, 8)) ||
      !equalBytes(packet.objectTag, hexToBytes(manifest.objectId).slice(0, 8)) ||
      packet.direction !== 'sender-to-receiver' ||
      packet.channel !== channel ||
      packet.sequence !== opened.binding.sequence ||
      packet.equationId !== opened.binding.symbolId
    ) {
      this.#integrityViolations += 1;
      this.ledger.forwardPayloadsRejected += 1;
      return [liveEvent('rejected', channel, 'Transport binding contradicts the authenticated envelope.')];
    }
    const generation = this.#generationFor(
      packet.generationId,
      packet.sourceCount,
      packet.symbolSize,
      packet.validGenerationBytes,
    );
    const admission = generation.decoder.add({
      generationId: packet.generationId,
      equationId: packet.equationId,
      sourceCount: packet.sourceCount,
      symbolSize: packet.symbolSize,
      validBytes: packet.validGenerationBytes,
      payload: packet.payload,
    });
    this.ledger.forwardPayloadsAccepted += 1;
    this.ledger.admittedEquations += 1;
    const events: LiveEvent[] = [];
    if (admission.duplicate || !admission.independent) {
      this.ledger.duplicates += 1;
      events.push(liveEvent('equation-duplicate', channel, `Equation ${packet.equationId} added no rank.`));
    } else {
      this.ledger.independentEquations += 1;
      if (channel === 'optical') generation.opticalContributions += 1;
      else generation.acousticContributions += 1;
      events.push(
        liveEvent(
          'equation-admitted',
          channel,
          `Generation ${packet.generationId} rank ${admission.rank}/${admission.requiredRank}.`,
        ),
      );
    }
    if (admission.reconstructed !== null && generation.reconstructed === null) {
      generation.reconstructed = admission.reconstructed;
      events.push(...(await this.#verifyGeneration(packet.generationId, generation, nowMs)));
    }
    return events;
  }

  /**
   * Feeds one payload captured from a forward physical channel: the raw O1
   * frame payload or a demodulated acoustic burst. Never throws on channel
   * garbage; every rejection is counted and reported as an event.
   */
  public async acceptForwardPayload(
    bytes: Uint8Array,
    channel: PhysicalChannel,
    nowMs = Date.now(),
  ): Promise<LiveEvent[]> {
    if (this.#phase === 'FAILED') return [];
    // Any CRC-valid payload proves the physical forward link is alive even
    // when the protocol outcome is a rejection; the watchdog needs that
    // distinction to give the operator a truthful diagnosis.
    this.#lastForwardPayloadAt = nowMs;
    // One call handles exactly one channel, so per-channel tallies can be
    // attributed from the aggregate deltas without touching every site.
    const before = {
      accepted: this.ledger.forwardPayloadsAccepted,
      rejected: this.ledger.forwardPayloadsRejected,
      duplicate: this.ledger.duplicates,
      independent: this.ledger.independentEquations,
    };
    try {
      return await this.#dispatchForwardPayload(bytes, channel, nowMs);
    } finally {
      const tally = this.ledger.channels[channel];
      tally.accepted += this.ledger.forwardPayloadsAccepted - before.accepted;
      tally.rejected += this.ledger.forwardPayloadsRejected - before.rejected;
      tally.duplicate += this.ledger.duplicates - before.duplicate;
      tally.independent += this.ledger.independentEquations - before.independent;
    }
  }

  async #dispatchForwardPayload(
    bytes: Uint8Array,
    channel: PhysicalChannel,
    nowMs: number,
  ): Promise<LiveEvent[]> {
    try {
      const magic = payloadMagic(bytes);
      if (magic === SBHS_MAGIC) {
        const type = bytes[6];
        if (type === 1) {
          // The sender keeps emitting its offer until the response lands;
          // seeing our own accepted offer again is expected, a different
          // offer mid-session is an anomaly worth counting.
          if (!equalBytes(bytes, this.#offerBytes)) {
            this.#integrityViolations += 1;
            return [liveEvent('rejected', channel, 'Conflicting discovery offer ignored.')];
          }
          return [liveEvent('confirmation-duplicate', channel, 'Repeated discovery offer.')];
        }
        if (type === 3) {
          return await this.#handleForwardConfirmation(bytes, nowMs);
        }
        this.ledger.forwardPayloadsRejected += 1;
        return [liveEvent('rejected', channel, `Unsupported SBHS type ${type ?? 'unknown'}.`)];
      }
      return await this.#handleEnvelope(bytes, channel, nowMs);
    } catch (error) {
      if (error instanceof SequenceReplayError) {
        this.ledger.duplicates += 1;
        return [liveEvent('equation-duplicate', channel, 'Repeated frame sequence ignored.')];
      }
      this.ledger.forwardPayloadsRejected += 1;
      if (error instanceof IntegrityError) {
        // Authenticated data that fails a sealed-object commitment is not
        // channel noise; the session is unsalvageable and must say so.
        this.#integrityViolations += 1;
        this.#fail(error.message, nowMs);
        return [liveEvent('rejected', channel, error.message)];
      }
      if (error instanceof ProtocolError) {
        return [liveEvent('rejected', channel, error.message)];
      }
      return [liveEvent('rejected', channel, 'Forward payload rejected.')];
    }
  }

  #buildFeedback(): RankFeedback {
    const manifest = this.#manifest;
    const rankDeficits = new Uint8Array(manifest?.chunkCount ?? 0);
    if (manifest !== null) {
      for (let index = 0; index < manifest.chunkCount; index += 1) {
        const generation = this.#generations.get(index);
        if (generation === undefined) {
          const entry = manifest.chunks[index];
          const symbolSize = 512;
          rankDeficits[index] = Math.min(
            255,
            Math.ceil((entry?.ciphertextLength ?? symbolSize) / symbolSize),
          );
        } else {
          rankDeficits[index] = Math.min(
            255,
            generation.decoder.sourceCount - generation.decoder.rank,
          );
        }
      }
    }
    const verified = this.#result !== null;
    return {
      manifestReceived: this.#manifest !== null,
      transferAuthorised: this.transferAuthorised,
      objectVerified: verified,
      forwardAudioDisabled: this.#forwardAudioDisabled,
      rankDeficits,
      receiverObjectHash: verified ? this.#result?.objectHash ?? null : null,
    };
  }

  /**
   * Produces the next payload for the return acoustic band. The cycle keeps
   * repeating the key response and both return confirmations until forward
   * traffic proves them delivered, then switches to rank feedback.
   */
  public async nextReturnPayload(): Promise<Uint8Array | null> {
    if (this.#phase === 'FAILED') return null;
    this.ledger.returnEmissions += 1;
    const preAuthorised = !this.transferAuthorised || this.#manifest === null;
    if (preAuthorised) {
      const step = this.#returnCycle % 3;
      this.#returnCycle += 1;
      if (step === 0) return this.#responseBytes.slice();
      const channel: PhysicalChannel = step === 1 ? 'optical' : 'acoustic';
      return createKeyConfirmation(
        this.#session,
        'receiver-to-sender',
        channel,
        this.#sequences.next('receiver-to-sender', channel),
      );
    }
    const key = this.#frameAuthKey;
    if (key === null) return null;
    const sequence = this.#sequences.next('receiver-to-sender', 'acoustic');
    return sealPhysicalFrameEnvelope(encodeRankFeedback(this.#buildFeedback()), key, {
      sessionId: this.#session.sessionId,
      direction: 'receiver-to-sender',
      channel: 'acoustic',
      sequence,
      symbolId: 0,
      profileId: ACOUSTIC_RETURN_PROFILE_ID,
      contextId: feedbackContextId(this.#session.transferId),
    });
  }
}
